import dayjs from 'dayjs';

import { injectLabelVariables } from './labelVariables';

const dynamicSort =
    (order = 'asc', sortType = null) =>
    (a, b) => {
        if (sortType === 'date') {
            return order === 'asc'
                ? new Date(a) - new Date(b)
                : new Date(b) - new Date(a);
        }

        if (sortType === 'number') {
            const numberA = Number(String(a).replace(/[^\d]/g, ''));
            const numberB = Number(String(b).replace(/[^\d]/g, ''));
            return order === 'asc' ? numberA - numberB : numberB - numberA;
        }

        return order === 'asc'
            ? String(a).localeCompare(String(b))
            : String(b).localeCompare(String(a));
    };

const formatDateValue = (value, formattingType) => {
    switch (formattingType) {
        case 'datetime':
            return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        case 'date':
            return dayjs(value).format('YYYY-MM-DD');
        case 'year':
            return dayjs(value).format('YYYY');
        case 'monthday':
            return dayjs(value).format('DD/MM');
        case 'monthyear':
            return dayjs(value).format('MM/YYYY');
        case 'human-readable':
            return dayjs(value).format('MMM D, YYYY');
        default:
            return value;
    }
};

const parseValue = (raw) =>
    typeof raw === 'number' ? raw : parseFloat(raw) || null;

const rowsToSeriesData = (rows, query, aggregate) => {
    const categoryField = query.serie.xAxisCategoryProperty || 'date';
    const valueField = query.serie.property || 'value';
    const categories = [];
    const values = [];

    if (aggregate) {
        const sums = new Map();
        rows.forEach((row) => {
            const category = row[categoryField];
            const value = parseValue(row[valueField]);
            if (value === null) {
                if (!sums.has(category)) sums.set(category, null);
                return;
            }
            const previous = sums.get(category);
            sums.set(category, previous == null ? value : previous + value);
        });
        sums.forEach((value, category) => {
            categories.push(category);
            values.push(value);
        });
    } else {
        rows.forEach((row) => {
            categories.push(row[categoryField]);
            values.push(parseValue(row[valueField]));
        });
    }

    return {
        xAxisCategories: categories,
        serie: { name: query.serie.label, data: values },
    };
};

// Sorted on the raw values so that, when several of them later format to the
// same label, the first row in the configured order is the one that label
// shows.
const sortAndClean = (seriesData, order, sortType) => {
    const pairs = seriesData.xAxisCategories
        .map((category, index) => ({
            category,
            value: seriesData.serie.data[index],
        }))
        .filter(({ value }) => value !== null)
        .sort((a, b) => dynamicSort(order, sortType)(a.category, b.category));

    return {
        xAxisCategories: pairs.map(({ category }) => category),
        serie: {
            name: seriesData.serie.name,
            data: pairs.map(({ value }) => value),
        },
    };
};

const padValues = (categories, seriesCategories, values) =>
    categories.map((category) => {
        const index = seriesCategories.indexOf(category);
        return index === -1 ? null : values[index];
    });

const constructChartZoneData = (
    zone,
    categories,
    { useCategoryIndex = true }
) => {
    const startIndex = categories.findIndex(
        (category) => String(category) === String(zone.startValue)
    );
    const endIndex = zone.endValue
        ? categories.findIndex(
              (category) => String(category) === String(zone.endValue)
          )
        : categories.length;
    const zones = [
        {
            value: useCategoryIndex
                ? endIndex
                : zone.endValue
                  ? Number(zone.endValue)
                  : undefined,
        },
        { value: useCategoryIndex ? startIndex : Number(zone.startValue) },
    ]
        .sort((a, b) => a.value - b.value)
        .filter((zoneItem) => typeof zoneItem.value !== 'undefined');

    if (zones.length > 1) zones[zones.length - 1].dashStyle = 'dot';
    else zones.push({ dashStyle: 'dot' });
    return zones;
};

const findClosestMatchingIndex = (categories, targetValue) => {
    const exactIndex = categories.indexOf(targetValue);
    if (exactIndex !== -1) return exactIndex;
    return categories.findIndex(
        (category) => String(category) === String(targetValue)
    );
};

const buildAnnotations = (series, categories) =>
    series
        .flatMap((currentSeries) =>
            (currentSeries.annotations?.annotationItems || [])
                .map((annotation) => {
                    const index = findClosestMatchingIndex(
                        categories,
                        annotation.xAxisCoord
                    );
                    const defaultYValue =
                        parseFloat(currentSeries.data[index]) || -1;
                    if (defaultYValue === -1) return null;
                    return {
                        allowOverlap: false,
                        point: {
                            xAxis: 0,
                            yAxis: 0,
                            x: index,
                            y: annotation.yAxisCoord
                                ? parseFloat(annotation.yAxisCoord)
                                : defaultYValue,
                        },
                        x: annotation.xAxisRelativeAlignment
                            ? parseFloat(annotation.xAxisRelativeAlignment)
                            : 0,
                        y: annotation.yAxisRelativeAlignment
                            ? parseFloat(annotation.yAxisRelativeAlignment)
                            : 0,
                        text: annotation.text,
                        style: {
                            color: '#575757ff',
                            fontSize: '12px',
                            fontWeight: 400,
                            zIndex: 10,
                        },
                        backgroundColor: 'transparent',
                        borderWidth: 1,
                        connectorColor: '#757575',
                        connectorWidth: 1,
                        connectorPadding: 4,
                    };
                })
                .filter(Boolean)
        )
        .map((labels) => ({
            draggable: false,
            labelOptions: { shape: 'connector' },
            labels: [labels],
            shapes: {
                point: {
                    xAxis: 0,
                    yAxis: 0,
                    x: labels.point.x,
                    y: labels.point.y,
                },
                type: 'circle',
                r: 4,
                fill: '#757575',
                strokeWidth: 1,
                stroke: '#fff',
            },
        }));

export const buildStandardChartOptions = (chartBlock, rowsByRequest) => {
    const queries = chartBlock.propertySeriesQueries || [];
    const seriesData = queries.map((query, index) =>
        sortAndClean(
            rowsToSeriesData(
                rowsByRequest[index] || [],
                query,
                !!chartBlock.aggregateDataByCategory
            ),
            chartBlock.xAxisCategoryOrdering,
            chartBlock.xAxisCategorySortingType
        )
    );
    const toLabel = (category) =>
        formatDateValue(category, chartBlock.xAxisLabelFormattting);
    const sortCategories = (values) =>
        values.sort(
            dynamicSort(
                chartBlock.xAxisCategoryOrdering,
                chartBlock.xAxisCategorySortingType
            )
        );
    const rawCategories = [
        ...new Set(
            seriesData.flatMap(({ xAxisCategories }) => xAxisCategories)
        ),
    ];
    // Date sorting must run on the raw values, since a formatted label such as
    // "12/2024" cannot be parsed as a date. The other sorting types keep
    // ordering the labels as displayed. Either way raw values that share a
    // label collapse into one category.
    const categories =
        chartBlock.xAxisCategorySortingType === 'date'
            ? [...new Set(sortCategories(rawCategories).map(toLabel))]
            : sortCategories([...new Set(rawCategories.map(toLabel))]);
    const hasNegatedSerie = queries.some(
        (query) => query.serie?.negateSerieValues
    );
    const series = seriesData.map((data, index) => {
        const query = queries[index];
        const paddedData = padValues(
            categories,
            data.xAxisCategories.map(toLabel),
            data.serie.data
        );
        const values = query.serie?.negateSerieValues
            ? paddedData.map((value) =>
                  typeof value === 'number' ? -value : value
              )
            : paddedData;
        const zone = query.serie?.zone;
        const zoneAxis =
            chartBlock.chartType !== 'bar'
                ? zone?.axis
                : zone?.axis === 'x'
                  ? 'y'
                  : 'x';
        return {
            ...(chartBlock.isMixedChart
                ? { type: query.seriesChartType || chartBlock.chartType }
                : {}),
            ...(chartBlock.isMixedChart &&
            (query.seriesChartType || chartBlock.chartType) === 'line'
                ? { connectNulls: true }
                : {}),
            name: injectLabelVariables(
                data.serie.name,
                chartBlock.labelVariables
            ),
            color: query.serie?.color,
            data: values,
            annotations:
                (query.annotations || []).find((annotation) =>
                    chartBlock.countryCode
                        ? annotation.showOnlyOnCountries?.includes(
                              chartBlock.countryCode.toUpperCase()
                          )
                        : !annotation.showOnlyOnCountries?.length
                ) ||
                (query.annotations || []).find(
                    (annotation) => !annotation.showOnlyOnCountries?.length
                ) ||
                [],
            ...(zone
                ? {
                      zoneAxis,
                      zones: constructChartZoneData(
                          zone,
                          zoneAxis === 'x' ? categories : values,
                          { useCategoryIndex: zoneAxis === 'x' }
                      ),
                  }
                : {}),
        };
    });
    const chartOptions = {
        series,
        xAxis:
            chartBlock.chartType === 'bar' && hasNegatedSerie
                ? [{ categories }, { categories, opposite: true, linkedTo: 0 }]
                : { categories },
    };
    const hasDecimalFmt =
        Number.isInteger(chartBlock.decimalPlaces) &&
        chartBlock.decimalPlaces >= 0;
    const yAxisOptions =
        chartBlock.yAxisMin != null ||
        chartBlock.yAxisMax != null ||
        chartBlock.yAxisTickInterval != null
            ? {
                  min: chartBlock.yAxisMin,
                  max: chartBlock.yAxisMax,
                  tickInterval: chartBlock.yAxisTickInterval,
              }
            : {};
    const plotOptions = hasDecimalFmt
        ? {
              ...chartBlock.plotOptions,
              series: {
                  ...(chartBlock.plotOptions?.series || {}),
                  dataLabels: {
                      ...(chartBlock.plotOptions?.series?.dataLabels || {}),
                      format: `{y:.${chartBlock.decimalPlaces}f}`,
                  },
              },
          }
        : chartBlock.plotOptions || {};
    return {
        ...chartOptions,
        title: { text: null },
        chart:
            chartBlock.chartType === 'polar'
                ? { type: chartBlock.polarChartValueType, polar: true }
                : chartBlock.isMixedChart
                  ? {}
                  : { type: chartBlock.chartType },
        exporting: chartBlock.exporting,
        plotOptions,
        ...(hasDecimalFmt
            ? { tooltip: { valueDecimals: chartBlock.decimalPlaces } }
            : {}),
        yAxis: {
            ...yAxisOptions,
            ...(hasDecimalFmt
                ? {
                      labels: {
                          format: `{value:.${chartBlock.decimalPlaces}f}`,
                      },
                  }
                : {}),
            title: {
                text: injectLabelVariables(
                    chartBlock.yAxisLabel,
                    chartBlock.labelVariables
                ),
            },
        },
        xAxis: Array.isArray(chartOptions.xAxis)
            ? chartOptions.xAxis.map((axis) => ({
                  ...axis,
                  title: {
                      text: injectLabelVariables(
                          chartBlock.xAxisLabel,
                          chartBlock.labelVariables
                      ),
                  },
              }))
            : {
                  ...chartOptions.xAxis,
                  title: {
                      text: injectLabelVariables(
                          chartBlock.xAxisLabel,
                          chartBlock.labelVariables
                      ),
                  },
              },
        annotations: buildAnnotations(series, categories),
    };
};
